// Mandats légaux (« gérant ») : libellés et aides pures (testées dans mandates.test.js). Le serveur reste la seule autorité.
// Deux axes qu'on ne mélange jamais : la SITUATION (dates) et la VÉRIFICATION (documents). Un mandat en cours non vérifié existe : il est « à vérifier ».

export const TIMING_LABELS = { upcoming: "À venir", in_force: "En cours", term_to_review: "Échéance à examiner", ended: "Terminé" };
export const TIMING_CLASSES = {
  upcoming: "bg-sky-50 text-sky-800 border-sky-200",
  in_force: "bg-emerald-50 text-emerald-800 border-emerald-200",
  term_to_review: "bg-amber-50 text-amber-800 border-amber-200",
  ended: "bg-slate-100 text-slate-600 border-slate-200",
};
export const VERIFICATION_LABELS = { draft: "Brouillon", to_verify: "À vérifier", verified: "Vérifié" };
export const VERIFICATION_CLASSES = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  to_verify: "bg-amber-50 text-amber-800 border-amber-200",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export const TERM_OPTIONS = [
  { value: "unknown", label: "Durée non déclarée" },
  { value: "statutes_silent", label: "Les statuts ne prévoient pas de durée" },
  { value: "statutes_fixed", label: "Les statuts fixent une échéance" },
  { value: "statutes_unlimited", label: "Durée indéterminée selon les statuts" },
];
export const SUPPLETIVE_YEARS = 4; // AUSCGIE art. 324, only when the statutes are declared silent

export const MISSING_LABELS = {
  evidence: "Au moins un justificatif que vous pouvez lire",
  appointment_date: "La date de l'acte de nomination",
  term: "La durée prévue par les statuts",
};

const DECLARED = ["in_force", "term_to_review"];
export const isDeclaredInForce = (m) => DECLARED.includes(m.timing);

// Current first (the ones in force, then the ones to come), then the ended ones, most recent start first (the server already sorts them).
export function splitMandates(items) {
  const current = items.filter((m) => m.timing !== "ended");
  const order = { in_force: 0, term_to_review: 0, upcoming: 1 };
  current.sort((a, b) => order[a.timing] - order[b.timing]);
  return { current, past: items.filter((m) => m.timing === "ended") };
}

// What a badge may say: « Gérant » once verified, « Gérant · à vérifier » otherwise (internal screens only; an external document never shows the latter).
export function mandateBadges(items) {
  return items.filter(isDeclaredInForce).map((m) => ({
    key: m.id, label: m.verification === "verified" ? m.type.label : `${m.type.label} · à vérifier`, verified: m.verification === "verified",
  }));
}

export function termSummary(m) {
  const { basis, due_date: due, indicative_due: indicative } = m.term || {};
  if (basis === "statutes_silent") return `Les statuts ne prévoient pas de durée : échéance légale supposée le ${formatDay(indicative)} (${SUPPLETIVE_YEARS} ans, art. 324 AUSCGIE). Le mandat n'est pas terminé pour autant : un humain doit vérifier les statuts et les actes.`;
  if (basis === "statutes_fixed") return `Échéance fixée par les statuts : ${formatDay(due)}.`;
  if (basis === "statutes_unlimited") return "Durée indéterminée selon les statuts.";
  return "Durée non déclarée : aucune échéance n'est supposée.";
}

export function formatDay(day) {
  if (!day) return "—";
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${y}`;
}

export const emptyForm = () => ({ type_id: "gerant", start_date: "", appointment_date: "", act_reference: "", term_basis: "unknown", term_due_date: "", term_note: "", evidence: [] });

export function formFromMandate(m) {
  return {
    type_id: m.type.id, start_date: m.start_date, appointment_date: m.appointment_date || "", act_reference: m.act_reference || "", term_basis: m.term.basis,
    term_due_date: m.term.due_date || "", term_note: m.term.note || "", evidence: (m.evidence || []).map((e) => ({ kind: e.kind, ref: e.ref, number: e.number, title: e.title, accessible: e.accessible })),
  };
}

export function isMandateForm(values, today, { creating = true } = {}) {
  if (creating && !values.type_id) return false;
  if (!values.start_date) return false;
  if (values.appointment_date && values.appointment_date > today) return false;
  if (values.term_basis === "statutes_fixed" && (!values.term_due_date || values.term_due_date < values.start_date)) return false;
  return true;
}

const termPayload = (values) => ({
  basis: values.term_basis, due_date: values.term_basis === "statutes_fixed" ? values.term_due_date : null, note: values.term_note.trim() || null,
});
const optional = (value) => (value && value.trim() ? value.trim() : null);

export function buildCreatePayload(values) {
  return {
    type_id: values.type_id, start_date: values.start_date, appointment_date: values.appointment_date || null, act_reference: optional(values.act_reference),
    term: termPayload(values), evidence: values.evidence.map((e) => ({ kind: e.kind, ref: e.ref })),
  };
}

// Only what changed: the server refuses a correction that changes nothing, and every correction needs its reason.
export function buildUpdatePayload(m, values, reason) {
  const before = formFromMandate(m);
  const payload = { expected_version: m.version, reason: reason.trim() };
  if (values.start_date !== before.start_date) payload.start_date = values.start_date;
  if ((values.appointment_date || "") !== before.appointment_date) payload.appointment_date = values.appointment_date || null;
  if ((optional(values.act_reference) || "") !== before.act_reference) payload.act_reference = optional(values.act_reference);
  const term = termPayload(values);
  const was = { basis: m.term.basis, due_date: m.term.due_date || null, note: m.term.note || null };
  if (JSON.stringify(term) !== JSON.stringify(was)) payload.term = term;
  const keys = (list) => list.map((e) => `${e.kind}:${e.ref}`).sort().join("|");
  if (keys(values.evidence) !== keys(before.evidence)) payload.evidence = values.evidence.map((e) => ({ kind: e.kind, ref: e.ref }));
  return payload;
}

export const hasChanges = (payload) => Object.keys(payload).some((k) => !["expected_version", "reason"].includes(k));

export const MIN_REASON = 3;
export const reasonOk = (reason) => (reason || "").trim().length >= MIN_REASON;

// The people leaving: the server answers `mandate_in_force` AFTER the exit; the dialog tells them BEFORE.
export function exitNotice(items) {
  const inForce = (items || []).filter(isDeclaredInForce);
  if (inForce.length === 0) return null;
  const names = inForce.map((m) => m.type.label).join(", ");
  return `Cette personne exerce un mandat légal (${names}). Sa sortie ne le termine pas : il reste en cours tant qu'un administrateur ne l'a pas régularisé. Pensez à l'acte de cessation et, le cas échéant, aux formalités au registre du commerce (RCCM).`;
}

export const HISTORY_LABELS = {
  mandate_created: "Mandat enregistré", mandate_updated: "Mandat corrigé", mandate_ended: "Mandat terminé", mandate_verified: "Vérification confirmée",
  mandate_unverified: "Vérification retirée",
};

export function verificationRefusal(info) {
  if (info?.code !== "verification_incomplete") return null;
  return (info.data?.missing || []).map((code) => MISSING_LABELS[code] || code);
}

export const EVIDENCE_KIND_LABELS = { governance_decision: "Décision", records_document: "Document" };
// The evidence a screen may offer follows the modules that are on: nothing is proposed that the server could not read.
export const evidenceKinds = (features) => [
  ...(features?.governance ? [{ value: "governance_decision", label: "Décision de gouvernance" }] : []),
  ...(features?.records ? [{ value: "records_document", label: "Document du registre" }] : []),
];

export const LIMIT_TEXT = (type) => (type.max_holders ? `${type.max_holders} titulaire${type.max_holders > 1 ? "s" : ""} au plus en même temps` : "Pas de limite de titulaires");
